import { Box, Typography, Stack, Paper, useTheme, Skeleton } from '@mui/material';
import { useGetAgencyBillingHistory } from '@/apis/agency/billing/hook';
import { formatDollars, titleCase } from '@/utils/helperFunctions';
import { EmptyStateView } from '@/components/Common/EmptyStateView';

export default function IntegrationUsage() {
  const theme = useTheme();
  const { data: BillingHistoryData, isLoading } = useGetAgencyBillingHistory();

  if (isLoading) {
    return <IntegrationUsageSkeleton />;
  }

  const integrations = BillingHistoryData?.integration_usage?.map((usage: any) => ({
    name: titleCase(usage.name),
    price: `${formatDollars(usage.unit_amount)}`,
    credentials: usage.quantity,
    total: `${formatDollars(usage.estimated_amount)}`
  }));

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'hidden',
        px: { xs: 0, lg: 2 }
      }}
    >
      <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
        Integration Usage Details
      </Typography>
      <Typography
        variant='body2'
        color='text.secondary'
        mb={2}
        mt={0.5}
        gutterBottom
        sx={{ fontSize: { xs: '14px', lg: 16 } }}
      >
        Current Month integration costs breakdown
      </Typography>

      <Stack spacing={2}>
        {integrations?.length ? (
          integrations.map((integration: any) => (
            <Paper
              key={integration.name}
              elevation={0}
              sx={{
                p: { xs: 1.5, lg: 2 },
                bgcolor: theme.palette.primary.light,
                borderRadius: 2,
                border: '1px solid #ececec'
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent='space-between'
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={{ xs: 1, sm: 0 }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={500} sx={{ fontSize: { xs: '0.875rem', lg: '1rem' } }}>
                    {integration.name}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    gutterBottom
                    sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}
                  >
                    {integration.price} (integration price) × {integration.credentials} (credentials)
                  </Typography>
                </Box>
                <Typography fontWeight='bold' sx={{ fontSize: { xs: '0.875rem', lg: '1rem' }, flexShrink: 0 }}>
                  {integration.total}
                </Typography>
              </Stack>
            </Paper>
          ))
        ) : (
          <EmptyStateView message='No integration found' />
        )}
      </Stack>
    </Box>
  );
}

function IntegrationUsageSkeleton() {
  const theme = useTheme();

  return (
    <Box marginX={{ xs: 0, lg: 2 }}>
      <Skeleton variant='text' width={220} height={28} />
      <Skeleton variant='text' width={300} height={18} sx={{ mb: 2 }} />

      <Stack spacing={2}>
        {[1, 2, 3].map((i) => (
          <Paper
            key={i}
            sx={{
              p: { xs: 1.5, lg: 2 },
              bgcolor: theme.palette.primary.light,
              borderRadius: 1.5
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent='space-between'
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              gap={{ xs: 1, sm: 0 }}
            >
              <Box sx={{ flex: 1 }}>
                <Skeleton variant='text' width={120} height={20} />
                <Skeleton variant='text' width={200} height={16} />
              </Box>
              <Skeleton variant='text' width={80} height={20} sx={{ flexShrink: 0 }} />
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
